import type { InputState } from './Physics';

type ButtonKind = 'left' | 'right' | 'go' | 'stop' | 'boost';

interface TouchButton {
  kind: ButtonKind;
  el: HTMLElement;
  pointerId: number | null;
}

export interface TouchInputOptions {
  /**
   * Called when any touch button is first pressed in a frame the racer
   * was idle. Mirrors keyboard's onAnyStart so the lap timer still
   * starts on the player's first input.
   */
  onAnyStart?: () => void;
}

/**
 * Four-button on-screen control surface for touch devices. Writes its
 * state into a plain `InputState` that `InputManager` reads each frame
 * alongside keyboard and gamepad input. The buttons themselves live in
 * `index.html` so the layout is purely CSS-driven.
 */
export class TouchInput {
  readonly state: InputState = { throttle: 0, brake: 0, steer: 0, boost: 0 };
  private readonly buttons: TouchButton[] = [];
  private readonly opts: TouchInputOptions;
  private readonly listenerCleanups: Array<() => void> = [];
  private active = false;

  constructor(opts: TouchInputOptions = {}) {
    this.opts = opts;
  }

  attach(): void {
    if (this.active) return;
    const kinds: ButtonKind[] = ['left', 'right', 'go', 'stop', 'boost'];
    for (const kind of kinds) {
      const el = document.getElementById(`touch-${kind}`);
      if (!el) continue;
      const btn: TouchButton = { kind, el, pointerId: null };
      this.buttons.push(btn);

      const down = (e: PointerEvent): void => this.handleDown(btn, e);
      const up = (e: PointerEvent): void => this.handleUp(btn, e);
      // iOS long-press detection lives in the touch-event layer; pointer
      // events are synthesised from it. Calling preventDefault on
      // pointerdown does NOT cancel the long-press → text-selection
      // magnifier gesture. We have to cancel the touch event itself,
      // which also stops Safari from synthesising the spurious
      // pointerleave / lostpointercapture that was releasing the button
      // mid-hold and leaving steering "stuck."
      const tStart = (e: TouchEvent): void => e.preventDefault();
      const ctx = (e: Event): void => e.preventDefault();

      el.addEventListener('pointerdown', down);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
      // No `pointerleave` here on purpose: iOS synthesises it during
      // gesture-recogniser glitches even while the finger is still on
      // the button, which dropped the input. Real releases come through
      // pointerup / pointercancel (per-element + window-level below).
      el.addEventListener('touchstart', tStart, { passive: false });
      el.addEventListener('contextmenu', ctx);

      this.listenerCleanups.push(() => {
        el.removeEventListener('pointerdown', down);
        el.removeEventListener('pointerup', up);
        el.removeEventListener('pointercancel', up);
        el.removeEventListener('touchstart', tStart);
        el.removeEventListener('contextmenu', ctx);
      });
    }

    // Window-level safety nets. Mobile browsers occasionally swallow
    // pointerup on the originating element when another gesture takes
    // over (notification shade pull, multi-touch confusion). Listening
    // on the window guarantees we still see *some* release event.
    const onWindowUp = (e: PointerEvent): void => {
      for (const b of this.buttons) {
        if (b.pointerId === e.pointerId) this.handleUp(b, e);
      }
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') this.reset();
    };
    const onBlur = (): void => this.reset();

    window.addEventListener('pointerup', onWindowUp, { passive: true });
    window.addEventListener('pointercancel', onWindowUp, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);

    this.listenerCleanups.push(() => {
      window.removeEventListener('pointerup', onWindowUp);
      window.removeEventListener('pointercancel', onWindowUp);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
    });

    this.active = true;
  }

  detach(): void {
    if (!this.active) return;
    for (const cleanup of this.listenerCleanups) cleanup();
    this.listenerCleanups.length = 0;
    this.buttons.length = 0;
    this.active = false;
    this.reset();
  }

  /** Snapshot of the touch axes for InputManager.update() to merge in. */
  getRaw(): Readonly<InputState> {
    return this.state;
  }

  /** Force-release everything (used when game is paused / loses focus). */
  reset(): void {
    for (const b of this.buttons) {
      if (b.pointerId !== null) {
        try { b.el.releasePointerCapture(b.pointerId); } catch { /* ignore */ }
      }
      b.pointerId = null;
      b.el.classList.remove('is-active');
    }
    this.recompute();
  }

  private handleDown(btn: TouchButton, e: PointerEvent): void {
    e.preventDefault();
    // If we're already tracking a different pointer on this button (e.g.
    // a multi-touch overlap, or a browser replaying a stale event),
    // release the old capture first. Otherwise its eventual pointerup
    // arrives with a now-mismatched pointerId and gets filtered as
    // "not mine" by handleUp, leaving the button stuck active.
    if (btn.pointerId !== null && btn.pointerId !== e.pointerId) {
      try { btn.el.releasePointerCapture(btn.pointerId); } catch { /* ignore */ }
    }
    btn.pointerId = e.pointerId;
    btn.el.classList.add('is-active');
    try { btn.el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    this.opts.onAnyStart?.();
    this.recompute();
  }

  private handleUp(btn: TouchButton, e: PointerEvent): void {
    // If we're tracking a different pointer for this button, ignore. But
    // if we're not tracking anything, still clear visual state (safety
    // net for synthesised events that arrive after a reset()).
    if (btn.pointerId !== null && btn.pointerId !== e.pointerId) return;
    if (btn.pointerId !== null) {
      try { btn.el.releasePointerCapture(btn.pointerId); } catch { /* ignore */ }
    }
    btn.pointerId = null;
    btn.el.classList.remove('is-active');
    this.recompute();
  }

  private recompute(): void {
    let left = 0;
    let right = 0;
    let go = 0;
    let stop = 0;
    let boost = 0;
    for (const b of this.buttons) {
      if (b.pointerId === null) continue;
      if (b.kind === 'left') left = 1;
      else if (b.kind === 'right') right = 1;
      else if (b.kind === 'go') go = 1;
      else if (b.kind === 'stop') stop = 1;
      else if (b.kind === 'boost') boost = 1;
    }
    this.state.throttle = go;
    this.state.brake = stop;
    this.state.steer = right - left;
    this.state.boost = boost;
  }
}

/** Heuristic: device has touch input. */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  if ('ontouchstart' in window) return true;
  const nav = window.navigator;
  if (nav && typeof nav.maxTouchPoints === 'number' && nav.maxTouchPoints > 0) return true;
  return false;
}
